import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtNum } from "@/lib/format";
import StatCard from "@/components/StatCard";
import { Loading, EmptyState, ChartTooltip } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { downloadCSV } from "@/lib/csv";
import ProductThumb, { imgSrc } from "@/components/ProductThumb";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { Ship, Package, Truck, CheckCircle2, Boxes, Plus, MapPin, Download, Eye, Globe, X, Pencil, Check, CalendarPlus } from "lucide-react";

const STATUS_COLORS = {
  DELIVERED: "#10B981", RECEIVING: "#06B6D4", SHIPPED: "#007AFF",
  IN_TRANSIT: "#F59E0B", WORKING: "#8B5CF6", MIXED: "#A3A3A3",
};
const BAR_COLORS = ["#007AFF", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#06B6D4"];
const COUNTRY_NAMES = { US: "United States", UK: "United Kingdom", DE: "Germany", FR: "France", ES: "Spain", IT: "Italy", CA: "Canada" };
const MONTHS = [["01", "Jan"], ["02", "Feb"], ["03", "Mar"], ["04", "Apr"], ["05", "May"], ["06", "Jun"],
  ["07", "Jul"], ["08", "Aug"], ["09", "Sep"], ["10", "Oct"], ["11", "Nov"], ["12", "Dec"]];

const FC_COUNTRY = [
  [/^(BHX|LTN|MAN|EMA|LBA|NCL|GLA|EDI|BRS|CWL|DXE|LCY|CBG)/, "UK"],
  [/^(FRA|DUS|LEJ|DTM|MUC|CGN|STR|HAM|BER|KSF|DOR)/, "DE"],
  [/^(ORY|CDG|LIL|MRS|LYS|BVA)/, "FR"],
  [/^(MXP|FCO|BLQ|TRN)/, "IT"],
  [/^(MAD|BCN|SVQ|VLC)/, "ES"],
];
function fcCountry(dest) {
  const d = (dest || "").toUpperCase();
  for (const [re, c] of FC_COUNTRY) if (re.test(d)) return c;
  return "US";
}
const countryOf = (r) => r.country || fcCountry(r.destination);

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || "#A3A3A3";
  return <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: `${c}26`, color: c }}>{status}</span>;
}

const selCls = "h-9 bg-[#111] border-[#262626] text-white rounded-full min-w-[130px]";

export default function ShipmentsTab({ onChanged }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [statusF, setStatusF] = useState("all");
  const [countryF, setCountryF] = useState("all");
  const [yearF, setYearF] = useState("all");
  const [monthF, setMonthF] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/shipments")
      .then((r) => { setRows(r.data.data); setLoading(false); })
      .catch(() => { toast.error("Could not load shipments."); setLoading(false); });
  }, []);
  useEffect(load, [load]);

  const updateStatus = async (shipment_id, status) => {
    try {
      await api.patch("/shipments/status", { shipment_id, status });
      setRows((prev) => prev.map((r) => (r.shipment_id === shipment_id ? { ...r, status } : r)));
      onChanged?.();
      toast.success("Shipment status updated");
    } catch { toast.error("Could not update status."); }
  };

  const setShipDate = async (shipment_id, date) => {
    try {
      await api.patch("/shipments/date", { shipment_id, date });
      setRows((prev) => prev.map((r) => (r.shipment_id === shipment_id ? { ...r, date } : r)));
      onChanged?.();
      toast.success("Shipment date updated");
    } catch { toast.error("Could not update date."); }
  };

  const setShipCountry = async (shipment_id, country) => {
    try {
      await api.patch("/shipments/country", { shipment_id, country });
      setRows((prev) => prev.map((r) => (r.shipment_id === shipment_id ? { ...r, country } : r)));
      onChanged?.();
      toast.success("Shipment country updated");
    } catch { toast.error("Could not update country."); }
  };

  const years = useMemo(() => Array.from(new Set(rows.map((r) => (r.date || "").slice(0, 4)).filter(Boolean))).sort().reverse(), [rows]);
  const countries = useMemo(() => Array.from(new Set(rows.map(countryOf))).sort(), [rows]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (statusF !== "all" && (r.status || "") !== statusF) return false;
    if (countryF !== "all" && countryOf(r) !== countryF) return false;
    const d = r.date || "";
    if (fromDate || toDate) {
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
    } else {
      if (yearF !== "all" && d.slice(0, 4) !== yearF) return false;
      if (monthF !== "all" && d.slice(5, 7) !== monthF) return false;
    }
    return true;
  }), [rows, statusF, countryF, yearF, monthF, fromDate, toDate]);

  const s = useMemo(() => {
    const byStatus = {}, byCountry = {}, byDest = {}, byDate = {}, bySku = {};
    let units = 0, delivered = 0, receiving = 0;
    const shipmentIds = new Set(), skus = new Set();
    for (const r of filtered) {
      const q = r.quantity || 0;
      units += q;
      if (r.shipment_id) shipmentIds.add(r.shipment_id);
      if (r.sku) { skus.add(r.sku); bySku[r.sku] = (bySku[r.sku] || 0) + q; }
      const st = r.status || "SHIPPED";
      byStatus[st] = (byStatus[st] || 0) + q;
      if (st === "DELIVERED") delivered += q;
      if (st === "RECEIVING") receiving += q;
      const cty = countryOf(r);
      byCountry[cty] = (byCountry[cty] || 0) + q;
      byDest[r.destination || "Unknown"] = (byDest[r.destination || "Unknown"] || 0) + q;
      if (r.date) byDate[r.date] = (byDate[r.date] || 0) + q;
    }
    return {
      units, delivered, receiving, shipments: shipmentIds.size, skus: skus.size,
      by_status: Object.entries(byStatus).map(([status, u]) => ({ status, units: u })),
      by_country: Object.entries(byCountry).map(([country, u]) => ({ country, name: COUNTRY_NAMES[country] || country, units: u })).sort((a, b) => b.units - a.units),
      by_destination: Object.entries(byDest).map(([destination, u]) => ({ destination, units: u })).sort((a, b) => b.units - a.units),
      timeseries: Object.entries(byDate).map(([date, u]) => ({ date, units: u })).sort((a, b) => a.date.localeCompare(b.date)),
      top_skus: Object.entries(bySku).map(([sku, u]) => ({ sku, units: u })).sort((a, b) => b.units - a.units).slice(0, 10),
    };
  }, [filtered]);

  const grouped = useMemo(() => {
    const m = {};
    for (const r of filtered) {
      const id = r.shipment_id || "—";
      if (!m[id]) m[id] = { shipment_id: id, items: [], units: 0, skus: new Set(), statuses: new Set(), destination: r.destination, country: countryOf(r), date: r.date };
      m[id].items.push(r);
      m[id].units += r.quantity || 0;
      m[id].skus.add(r.sku);
      if (r.status) m[id].statuses.add(r.status);
      if (r.destination) m[id].destination = r.destination;
    }
    return Object.values(m).map((g) => ({ ...g, skuCount: g.skus.size, status: g.statuses.size > 1 ? "MIXED" : ([...g.statuses][0] || "SHIPPED") }));
  }, [filtered]);

  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  if (loading) return <Loading />;

  const sortArrow = (k) => (sort.key === k ? (sort.dir === "asc" ? " ↑" : " ↓") : "");
  const sortShip = (k) => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "desc" ? "asc" : "desc" }));
  const sortedRows = [...filtered].sort((a, b) => {
    const av = sort.key === "quantity" ? (a.quantity || 0) : (a.date || "");
    const bv = sort.key === "quantity" ? (b.quantity || 0) : (b.date || "");
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sort.dir === "asc" ? cmp : -cmp;
  });

  const filtersActive = statusF !== "all" || countryF !== "all" || yearF !== "all" || monthF !== "all" || fromDate || toDate;
  const clearFilters = () => { setStatusF("all"); setCountryF("all"); setYearF("all"); setMonthF("all"); setFromDate(""); setToDate(""); };
  const exportCsv = () => downloadCSV("fba-shipments.csv", filtered.map((r) => ({
    shipment_id: r.shipment_id, sku: r.sku, product_name: r.product_name, quantity: r.quantity,
    date_dispatched: r.date, destination: r.destination, country: countryOf(r), status: r.status,
    image_url: imgSrc(r.image) || "",
  })));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-[#A3A3A3] max-w-md">Track every unit sent to Amazon FBA across all marketplaces.</p>
        <div className="flex items-center gap-2">
          {!!filtered.length && (
            <Button data-testid="export-shipments-csv" onClick={exportCsv} variant="outline" className="h-9 bg-[#111] border-[#262626] text-white hover:bg-[#1a1a1a] hover:text-white rounded-full">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
          )}
          <AddShipmentDialog onSaved={() => { load(); onChanged?.(); }} />
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-[#0f0f0f] border border-white/5 rounded-xl p-4 flex flex-wrap items-end gap-3" data-testid="shipments-filter-bar">
        <FilterField label="Status">
          <Select value={statusF} onValueChange={setStatusF}>
            <SelectTrigger data-testid="filter-status" className={selCls}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111] border-[#262626] text-white">
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="SHIPPED">Shipped</SelectItem>
              <SelectItem value="RECEIVING">Receiving</SelectItem>
              <SelectItem value="DELIVERED">Delivered</SelectItem>
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Country / Marketplace">
          <Select value={countryF} onValueChange={setCountryF}>
            <SelectTrigger data-testid="filter-country" className={selCls}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111] border-[#262626] text-white">
              <SelectItem value="all">All countries</SelectItem>
              {countries.map((c) => <SelectItem key={c} value={c}>{COUNTRY_NAMES[c] || c}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Year">
          <Select value={yearF} onValueChange={setYearF} disabled={!!(fromDate || toDate)}>
            <SelectTrigger data-testid="filter-year" className={`${selCls} min-w-[100px]`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111] border-[#262626] text-white">
              <SelectItem value="all">All years</SelectItem>
              {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Month">
          <Select value={monthF} onValueChange={setMonthF} disabled={!!(fromDate || toDate)}>
            <SelectTrigger data-testid="filter-month" className={`${selCls} min-w-[110px]`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111] border-[#262626] text-white">
              <SelectItem value="all">All months</SelectItem>
              {MONTHS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Custom from">
          <Input data-testid="filter-from" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 bg-[#111] border-[#262626] text-white rounded-full" />
        </FilterField>
        <FilterField label="Custom to">
          <Input data-testid="filter-to" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 bg-[#111] border-[#262626] text-white rounded-full" />
        </FilterField>
        {filtersActive && (
          <Button data-testid="filter-clear" onClick={clearFilters} variant="ghost" className="h-9 text-[#A3A3A3] hover:text-white">
            <X className="h-4 w-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      {!filtered.length ? (
        <EmptyState title="No shipments match" message="No FBA shipments for the selected filters. Adjust filters, upload an 'FBA Inbound Shipment Items' report, or add one manually." />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard testid="shp-total-units" label="Units Shipped" value={fmtNum(s.units)} sub="to Amazon FBA" icon={Package} accent="#007AFF" />
            <StatCard testid="shp-total" label="Shipments" value={fmtNum(s.shipments)} sub={`${fmtNum(s.skus)} SKUs`} icon={Ship} accent="#8B5CF6" />
            <StatCard testid="shp-receiving" label="Receiving" value={fmtNum(s.receiving)} sub="units checking in" icon={Truck} accent="#06B6D4" />
            <StatCard testid="shp-delivered" label="Delivered" value={fmtNum(s.delivered)} sub="units received" icon={CheckCircle2} accent="#10B981" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-[#0f0f0f] border border-white/5 rounded-xl p-5" data-testid="shipments-timeseries">
              <h3 className="font-head font-bold text-white mb-1">Units Dispatched Over Time</h3>
              <p className="text-xs text-[#666] mb-4">Filtered by date / month / year selection</p>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={s.timeseries} margin={{ left: -12, right: 8, top: 8 }}>
                  <defs><linearGradient id="shpU" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#007AFF" stopOpacity={0.4} /><stop offset="100%" stopColor="#007AFF" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#1a1a1a" }} minTickGap={30} />
                  <YAxis tick={{ fill: "#666", fontSize: 11 }} tickLine={false} axisLine={false} />
                  <Tooltip content={<ChartTooltip currency="USD" />} />
                  <Area type="monotone" dataKey="units" name="Units" stroke="#007AFF" strokeWidth={2} fill="url(#shpU)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-[#0f0f0f] border border-white/5 rounded-xl p-5" data-testid="shipments-by-status">
              <h3 className="font-head font-bold text-white mb-1">By Status</h3>
              <p className="text-xs text-[#666] mb-4">Units per status</p>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={s.by_status} dataKey="units" nameKey="status" innerRadius={50} outerRadius={82} paddingAngle={2}>
                    {s.by_status.map((p, i) => <Cell key={i} fill={STATUS_COLORS[p.status] || BAR_COLORS[i % BAR_COLORS.length]} stroke="#0f0f0f" />)}
                  </Pie>
                  <Tooltip content={<ChartTooltip currency="USD" />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {s.by_status.map((p, i) => (
                  <div key={p.status} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2 text-[#A3A3A3]"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: STATUS_COLORS[p.status] || BAR_COLORS[i % BAR_COLORS.length] }} />{p.status}</span>
                    <span className="tnum text-white font-semibold">{fmtNum(p.units)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-[#0f0f0f] border border-white/5 rounded-xl p-5" data-testid="shipments-by-country">
              <div className="flex items-center gap-2 mb-1"><Globe className="h-4 w-4 text-[#007AFF]" /><h3 className="font-head font-bold text-white">By Country / Marketplace</h3></div>
              <p className="text-xs text-[#666] mb-4">Units shipped per destination country</p>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={s.by_country} layout="vertical" margin={{ left: 20, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" horizontal={false} />
                  <XAxis type="number" tick={{ fill: "#666", fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fill: "#A3A3A3", fontSize: 11 }} tickLine={false} axisLine={false} width={95} />
                  <Tooltip cursor={{ fill: "#ffffff08" }} content={<ChartTooltip currency="USD" />} />
                  <Bar dataKey="units" name="Units" radius={[0, 4, 4, 0]}>{s.by_country.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-[#0f0f0f] border border-white/5 rounded-xl p-5" data-testid="shipments-by-destination">
              <div className="flex items-center gap-2 mb-1"><MapPin className="h-4 w-4 text-[#10B981]" /><h3 className="font-head font-bold text-white">By Destination (FC)</h3></div>
              <p className="text-xs text-[#666] mb-4">Units per fulfilment center</p>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={s.by_destination} layout="vertical" margin={{ left: 10, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" horizontal={false} />
                  <XAxis type="number" tick={{ fill: "#666", fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="destination" tick={{ fill: "#A3A3A3", fontSize: 11 }} tickLine={false} axisLine={false} width={80} />
                  <Tooltip cursor={{ fill: "#ffffff08" }} content={<ChartTooltip currency="USD" />} />
                  <Bar dataKey="units" name="Units" radius={[0, 4, 4, 0]}>{s.by_destination.map((_, i) => <Cell key={i} fill={BAR_COLORS[(i + 2) % BAR_COLORS.length]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden" data-testid="shipments-by-id-table">
            <div className="p-5 border-b border-[#1a1a1a]"><h3 className="font-head font-bold text-white">Shipments by ID</h3><p className="text-xs text-[#666]">Click an ID to view its contents</p></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[#666] text-xs uppercase tracking-wider">
                  <th className="px-5 py-3">Shipment ID</th><th className="px-5 py-3">Date Dispatched</th><th className="px-5 py-3">Country</th>
                  <th className="px-5 py-3">Destination</th><th className="px-5 py-3 text-right">SKUs</th><th className="px-5 py-3 text-right">Units</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Details</th>
                </tr></thead>
                <tbody>
                  {grouped.map((g) => (
                    <tr key={g.shipment_id} className="border-t border-[#141414] hover:bg-white/5 transition-colors duration-150">
                      <td className="px-5 py-3"><button data-testid={`shipment-link-${g.shipment_id}`} onClick={() => setSelectedId(g.shipment_id)} className="text-[#007AFF] hover:text-[#4da3ff] tnum text-xs font-semibold hover:underline transition-colors duration-150">{g.shipment_id}</button></td>
                      <td className="px-5 py-3"><EditableDate value={g.date} onSave={(d) => setShipDate(g.shipment_id, d)} testid={`ship-date-${g.shipment_id}`} /></td>
                      <td className="px-5 py-3">
                        <Select value={g.country || "US"} onValueChange={(v) => setShipCountry(g.shipment_id, v)}>
                          <SelectTrigger data-testid={`ship-country-${g.shipment_id}`} className="h-8 w-[120px] bg-[#111] border-[#262626] text-white rounded-full text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#111] border-[#262626] text-white">
                            {["US", "UK", "DE", "FR", "ES", "IT", "CA"].map((c) => <SelectItem key={c} value={c}>{COUNTRY_NAMES[c] || c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-3 text-[#A3A3A3] tnum">{g.destination || "—"}</td>
                      <td className="px-5 py-3 text-right tnum text-[#A3A3A3]">{fmtNum(g.skuCount)}</td>
                      <td className="px-5 py-3 text-right tnum text-white font-semibold">{fmtNum(g.units)}</td>
                      <td className="px-5 py-3">
                        <Select value={g.status === "MIXED" ? undefined : g.status} onValueChange={(v) => updateStatus(g.shipment_id, v)}>
                          <SelectTrigger data-testid={`status-select-${g.shipment_id}`} className="h-8 w-[132px] bg-[#111] border-[#262626] text-white rounded-full text-xs">
                            <SelectValue placeholder="MIXED" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#111] border-[#262626] text-white">
                            <SelectItem value="SHIPPED">Shipped</SelectItem>
                            <SelectItem value="RECEIVING">Receiving</SelectItem>
                            <SelectItem value="DELIVERED">Delivered</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-3 text-right"><button data-testid={`view-shipment-${g.shipment_id}`} onClick={() => setSelectedId(g.shipment_id)} className="inline-flex items-center gap-1 text-[#A3A3A3] hover:text-[#007AFF] text-xs font-semibold transition-colors duration-150"><Eye className="h-3.5 w-3.5" /> View</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden" data-testid="shipments-table">
            <div className="p-5 border-b border-[#1a1a1a]"><h3 className="font-head font-bold text-white">All Shipments</h3><p className="text-xs text-[#666]">Every SKU dispatched to Amazon FBA</p></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[#666] text-xs uppercase tracking-wider">
                  <th className="px-5 py-3">Shipment ID</th>
                  <th className="px-5 py-3"><button data-testid="sort-ship-date" onClick={() => sortShip("date")} className="uppercase tracking-wider hover:text-white transition-colors">Date Dispatched{sortArrow("date")}</button></th>
                  <th className="px-5 py-3">Product</th><th className="px-5 py-3">SKU</th>
                  <th className="px-5 py-3">Country</th><th className="px-5 py-3">Destination</th>
                  <th className="px-5 py-3 text-right"><button data-testid="sort-ship-qty" onClick={() => sortShip("quantity")} className="uppercase tracking-wider hover:text-white transition-colors">Qty{sortArrow("quantity")}</button></th>
                  <th className="px-5 py-3">Status</th>
                </tr></thead>
                <tbody>
                  {sortedRows.map((r) => (
                    <tr key={r.id} className="border-t border-[#141414] hover:bg-white/5 transition-colors duration-150">
                      <td className="px-5 py-3"><button data-testid={`shipment-cell-${r.shipment_id}`} onClick={() => setSelectedId(r.shipment_id)} className="text-[#007AFF] hover:text-[#4da3ff] tnum text-xs font-medium hover:underline transition-colors duration-150">{r.shipment_id}</button></td>
                      <td className="px-5 py-3 text-[#A3A3A3] tnum text-xs">{r.date}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2 max-w-[240px]">
                          <ProductThumb src={r.image} size={28} title={r.product_name} />
                          <span className="text-white truncate" title={r.product_name}>{r.product_name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-[#A3A3A3] tnum text-xs">{r.sku}</td>
                      <td className="px-5 py-3 text-[#A3A3A3] text-xs">{COUNTRY_NAMES[countryOf(r)] || countryOf(r)}</td>
                      <td className="px-5 py-3 text-[#A3A3A3] tnum">{r.destination || "—"}</td>
                      <td className="px-5 py-3 text-right tnum text-white font-semibold">{fmtNum(r.quantity)}</td>
                      <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <ShipmentDetailsDialog group={grouped.find((g) => g.shipment_id === selectedId) || null} onClose={() => setSelectedId(null)} />
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-widest text-[#666]">{label}</Label>
      {children}
    </div>
  );
}

function EditableDate({ value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [d, setD] = useState(value || "");
  useEffect(() => setD(value || ""), [value]);
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <Input data-testid={`${testid}-input`} type="date" value={d} onChange={(e) => setD(e.target.value)} className="h-7 w-[140px] bg-[#111] border-[#262626] text-white text-xs" />
        <button data-testid={`${testid}-save`} onClick={() => { if (d) { onSave(d); setEditing(false); } }} className="text-[#10B981] hover:text-[#0d9668]"><Check className="h-4 w-4" /></button>
        <button data-testid={`${testid}-cancel`} onClick={() => { setD(value || ""); setEditing(false); }} className="text-[#A3A3A3] hover:text-[#EF4444]"><X className="h-4 w-4" /></button>
      </span>
    );
  }
  return value
    ? <button data-testid={testid} onClick={() => setEditing(true)} className="text-[#A3A3A3] hover:text-white tnum text-xs inline-flex items-center gap-1 group"><span>{value}</span><Pencil className="h-3 w-3 opacity-0 group-hover:opacity-60" /></button>
    : <button data-testid={`${testid}-set`} onClick={() => setEditing(true)} className="inline-flex items-center gap-1 text-[#F59E0B] hover:text-[#fbbf24] text-xs font-semibold"><CalendarPlus className="h-3.5 w-3.5" /> Set date</button>;
}

function ShipmentDetailsDialog({ group, onClose }) {
  return (
    <Dialog open={!!group} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0d0d0d] border-[#262626] text-white max-w-2xl" data-testid="shipment-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><Ship className="h-5 w-5 text-[#007AFF]" /> {group?.shipment_id}</DialogTitle>
          <DialogDescription className="text-[#A3A3A3]">Contents of this inbound FBA shipment.</DialogDescription>
        </DialogHeader>
        {group && (
          <>
            <div className="grid grid-cols-4 gap-3">
              <Info label="Date" value={group.date} />
              <Info label="Country" value={COUNTRY_NAMES[group.country] || group.country} />
              <Info label="Destination" value={group.destination || "—"} />
              <Info label="Total Units" value={fmtNum(group.units)} />
            </div>
            <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-[#262626]" data-testid="shipment-details-items">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#111]"><tr className="text-left text-[#666] text-xs uppercase tracking-wider">
                  <th className="px-4 py-2">Product</th><th className="px-4 py-2">SKU</th><th className="px-4 py-2 text-right">Qty</th><th className="px-4 py-2">Status</th>
                </tr></thead>
                <tbody>
                  {group.items.map((it) => (
                    <tr key={it.id} className="border-t border-[#141414]">
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2 max-w-[260px]">
                          <ProductThumb src={it.image} size={28} title={it.product_name} />
                          <span className="text-white truncate" title={it.product_name}>{it.product_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-[#A3A3A3] tnum text-xs">{it.sku}</td>
                      <td className="px-4 py-2 text-right tnum text-white">{fmtNum(it.quantity)}</td>
                      <td className="px-4 py-2"><StatusBadge status={it.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-[#111] border border-[#262626] rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-widest text-[#666]">{label}</p>
      <p className="font-semibold tnum text-sm">{value}</p>
    </div>
  );
}

function AddShipmentDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const blank = { shipment_id: "", sku: "", product_name: "", quantity: "", destination: "", country: "US", date: "", status: "SHIPPED" };
  const [f, setF] = useState(blank);
  const save = async () => {
    try {
      await api.post("/shipments", { ...f, quantity: Number(f.quantity) });
      toast.success("Shipment saved");
      setOpen(false); setF(blank);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const row = (label, node) => (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">{label}</Label>{node}</div>);
  const cls = "bg-[#111] border-[#262626]";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-shipment-button" className="h-9 bg-[#007AFF] hover:bg-[#005BB5] rounded-full px-4 font-semibold"><Plus className="h-4 w-4 mr-1" /> Add Shipment</Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0d0d0d] border-[#262626] text-white" data-testid="add-shipment-dialog">
        <DialogHeader><DialogTitle className="font-head">Log FBA Inbound Shipment</DialogTitle>
          <DialogDescription className="text-[#A3A3A3]">Record a shipment of units sent to Amazon FBA.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {row("Product name", <Input data-testid="shipment-name" value={f.product_name} onChange={(e) => setF({ ...f, product_name: e.target.value })} className={cls} />)}
          <div className="grid grid-cols-2 gap-3">
            {row("SKU", <Input data-testid="shipment-sku" value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} className={cls} />)}
            {row("Shipment ID", <Input data-testid="shipment-id" value={f.shipment_id} onChange={(e) => setF({ ...f, shipment_id: e.target.value })} placeholder="auto if blank" className={cls} />)}
            {row("Quantity", <Input data-testid="shipment-qty" type="number" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} className={cls} />)}
            {row("Date dispatched", <Input data-testid="shipment-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={cls} />)}
            {row("Destination FC", <Input data-testid="shipment-dest" value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} placeholder="ONT8 / BHX4" className={cls} />)}
            {row("Country", (
              <Select value={f.country} onValueChange={(v) => setF({ ...f, country: v })}>
                <SelectTrigger data-testid="shipment-country" className={cls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111] border-[#262626] text-white">
                  {["US", "UK", "DE", "FR", "ES", "IT", "CA"].map((c) => <SelectItem key={c} value={c}>{COUNTRY_NAMES[c] || c}</SelectItem>)}
                </SelectContent>
              </Select>
            ))}
            {row("Status", (
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="shipment-status" className={cls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111] border-[#262626] text-white">
                  <SelectItem value="SHIPPED">Shipped</SelectItem>
                  <SelectItem value="RECEIVING">Receiving</SelectItem>
                  <SelectItem value="DELIVERED">Delivered</SelectItem>
                </SelectContent>
              </Select>
            ))}
          </div>
        </div>
        <DialogFooter><Button data-testid="save-shipment" onClick={save} className="bg-[#10B981] hover:bg-[#0d9668] text-black">Save shipment</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
